// Canonical "is this invoice paid?" predicate — the single source of truth the
// approval layer and the payment (mBank outbound) layer BOTH consult, so they can
// never disagree about which invoices are still owed.
//
// Dependency-free (no DAOs, no Supabase) so it imports cleanly into the Deno edge
// functions (mbank-sync / mbank-send-payment), the shared services, and the React
// frontend alike.
//
// Why both fields? `invoices.payment_status` is the six-status payment ledger
// (pending | in_payment | on_hold | partially_paid | paid | overpaid), derived in
// the app layer by paymentStatusFold.ts from bank evidence. Fakturownia marks an
// invoice paid the moment it's recorded there — long before, or entirely
// independently of, any bank-side reconciliation. An invoice can therefore be
// FA-paid while its local payment_status still reads 'pending'/'partially_paid'
// (the bank pipeline never ran). Paying it again would double-pay the supplier.
//
// This mirrors InvoiceApprovalService.isPaid() — that method now delegates here so
// the two layers share one definition.

/** The minimal shape the paid check needs. Both InvoiceRow and the edge-function
 *  row projections satisfy it. */
export interface InvoicePaidFields {
  payment_status?: string | null;
  fakturownia_payment_status?: string | null;
}

/**
 * True when the invoice is paid per EITHER the bank-driven local status
 * (`paid` or `overpaid` — both mean the invoice is fully covered) OR
 * Fakturownia. Payment gates (mBank prepare / send / isMbankPayable) must
 * exclude anything this returns true for — regardless of what the local
 * payment_status alone says.
 */
export function isInvoicePaid(inv: InvoicePaidFields): boolean {
  return (
    inv.payment_status === 'paid' ||
    inv.payment_status === 'overpaid' ||
    inv.fakturownia_payment_status === 'paid'
  );
}

/** Local statuses that mean "not (yet) fully paid" — Fakturownia may still claim
 *  paid on top of them. */
const OPEN_PAYMENT_STATUSES: readonly string[] = [
  'pending',
  'partially_paid',
  'on_hold',
  'in_payment',
];

/**
 * True when Fakturownia reports the invoice paid while the local ledger status is
 * still open (`pending | partially_paid | on_hold | in_payment`, or absent) — i.e.
 * the ledger has not (yet) caught up with FA. This is the display-side companion
 * to {@link isInvoicePaid}: the gates already refuse to pay such an invoice, so
 * the view shows the ledger status AND a separate "FA: opłacona" marker instead
 * of silently merging them. False once the ledger itself says `paid`/`overpaid`.
 */
export function isFaOnlyPaid(inv: InvoicePaidFields): boolean {
  return (
    inv.fakturownia_payment_status === 'paid' &&
    (inv.payment_status == null || OPEN_PAYMENT_STATUSES.includes(inv.payment_status))
  );
}
